import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { MathJaxContext } from 'better-react-mathjax';
import { mathJaxConfig } from './utils/safeMathJaxSetup'; // MathJax配置
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import SupervisorManagePage from './pages/SupervisorManagePage';
import QuestionsPage from './pages/QuestionsPage';
import MigrationPage from './pages/MigrationPage';

import SubjectManagePage from './pages/SubjectManagePage';
import SubjectVersionManagePage from './pages/SubjectVersionManagePage';
import ChapterManagePage from './pages/ChapterManagePage';
import KnowledgePointManagePage from './pages/KnowledgePointManagePage';
import CardPrototypeManagePage from './pages/CardPrototypeManagePage';
import MathKeyboardTestPage from './pages/MathKeyboardTestPage';
import GlobalKeyboard from './components/GlobalKeyboard';


// 创建主题
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  zIndex: {
    // 确保自定义键盘的z-index高于所有Material-UI组件
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    // 确保Dialog的z-index不会超过键盘
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 1300, // 确保Dialog在键盘之下
        },
      },
    },
  },
});


function App() {
  const handleGlobalError = (_error: Error, _errorInfo: React.ErrorInfo) => {
    // 可以在这里集成错误监控服务
    // errorMonitoringService.captureException(error, {
    //   extra: errorInfo,
    //   tags: { component: 'App' }
    // });
  };


  return (
    <ErrorBoundary onError={handleGlobalError}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MathJaxContext config={mathJaxConfig} version={3}>
          <AuthProvider>
          <Router>
            <Routes>
              {/* 公开路由 */}
              <Route path="/login" element={<LoginPage />} />

              {/* 受保护的路由 */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <DashboardPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <ErrorBoundary>
                      <UsersPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/supervisors"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary>
                      <SupervisorManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/questions"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <ErrorBoundary>
                      <QuestionsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* <Route
                path="/migration"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <ErrorBoundary>
                      <MigrationPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              /> */}



              <Route
                path="/subjects"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary>
                      <SubjectManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subject-versions"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary>
                      <SubjectVersionManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/chapters"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary>
                      <ChapterManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/knowledge-points"
                element={
                  <ProtectedRoute requireAdmin>
                    <ErrorBoundary>
                      <KnowledgePointManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/card-prototypes"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <ErrorBoundary>
                      <CardPrototypeManagePage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/math-keyboard-test"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <MathKeyboardTestPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* 默认重定向 */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Router>
          </AuthProvider>

          {/* 全局键盘组件 */}
          <GlobalKeyboard />
        </MathJaxContext>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
